export type LetterVariant = {
	char: string;
	weight: number;
	style: "normal" | "italic" | "oblique";
	caps: "normal" | "small-caps" | "petite-caps";
	stretch: "normal" | "condensed" | "extra-condensed" | "expanded";
};

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
const STRETCHES: LetterVariant["stretch"][] = [
	"normal",
	"condensed",
	"extra-condensed",
	"expanded",
];
const NO_SLANT = new Set(["I", "i", "l", "1", "ı", "Ι", "І", "і", "/", "|"]);

function pick<T>(items: readonly T[]) {
	return items[Math.floor(Math.random() * items.length)];
}

function baseLetter(letter: string) {
	return letter
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toUpperCase();
}

const LETTER_GLYPHS: Record<string, string[]> = {
	A: [
		"A",
		"a",
		"Á",
		"À",
		"Â",
		"Ä",
		"Å",
		"Ā",
		"Ă",
		"Ą",
		"Ã",
		"Ǎ",
		"Ǟ",
		"Ǡ",
		"Ǻ",
		"Ȁ",
		"Ȃ",
		"Ȧ",
		"Ⱥ",
		"á",
		"à",
		"â",
		"ä",
		"å",
		"ā",
		"ă",
		"ą",
		"ã",
		"ǎ",
		"ǟ",
		"ǡ",
		"ǻ",
		"ȁ",
		"ȃ",
		"ȧ",
		"ⱥ",
		"Ấ",
		"Ầ",
		"Ẩ",
		"Ẫ",
		"Ậ",
		"Ắ",
		"Ằ",
		"Ẳ",
		"Ẵ",
		"Ặ",
		"Ạ",
		"Ả",
		"ấ",
		"ầ",
		"ẩ",
		"ẫ",
		"ậ",
		"ắ",
		"ằ",
		"ẳ",
		"ẵ",
		"ặ",
		"ạ",
		"ả",
		"Α",
		"α",
		"А",
		"а",
		"ᴀ",
		"ª",
		"Å",
	],
	B: [
		"B",
		"b",
		"Ḃ",
		"ḃ",
		"Ḅ",
		"ḅ",
		"Ḇ",
		"ḇ",
		"Ƀ",
		"ƀ",
		"Ɓ",
		"ɓ",
		"ß",
		"Β",
		"β",
		"Б",
		"б",
		"в",
		"ᴃ",
		"ʙ",
	],
	C: [
		"C",
		"c",
		"Ç",
		"ç",
		"Ć",
		"ć",
		"Ĉ",
		"ĉ",
		"Ċ",
		"ċ",
		"Č",
		"č",
		"Ḉ",
		"ḉ",
		"Ȼ",
		"ȼ",
		"©",
		"¢",
		"С",
		"с",
		"Ϲ",
		"ϲ",
		"ᴄ",
	],
	D: [
		"D",
		"d",
		"Ď",
		"ď",
		"Đ",
		"đ",
		"Ḋ",
		"ḋ",
		"Ḍ",
		"ḍ",
		"Ḏ",
		"ḏ",
		"Ḑ",
		"ḑ",
		"Ḓ",
		"ḓ",
		"Ð",
		"ð",
		"Ɖ",
		"Ɗ",
		"Δ",
		"δ",
		"Д",
		"д",
		"ᴅ",
	],
	E: [
		"E",
		"e",
		"É",
		"È",
		"Ê",
		"Ë",
		"Ē",
		"Ė",
		"Ę",
		"Ě",
		"Ĕ",
		"Ȩ",
		"Ɇ",
		"Ȅ",
		"Ȇ",
		"é",
		"è",
		"ê",
		"ë",
		"ē",
		"ė",
		"ę",
		"ě",
		"ĕ",
		"ȩ",
		"ɇ",
		"ȅ",
		"ȇ",
		"Ế",
		"Ề",
		"Ể",
		"Ễ",
		"Ệ",
		"Ẹ",
		"Ẻ",
		"Ẽ",
		"ế",
		"ề",
		"ể",
		"ễ",
		"ệ",
		"ẹ",
		"ẻ",
		"ẽ",
		"Ε",
		"Е",
		"е",
		"ᴇ",
		"ɛ",
		"€",
	],
	F: ["F", "f", "Ḟ", "ḟ", "Ƒ", "ƒ", "ﬀ", "₣", "Φ", "φ", "Ф", "ф", "ꜰ"],
	G: [
		"G",
		"g",
		"Ǵ",
		"ǵ",
		"Ğ",
		"ğ",
		"Ĝ",
		"ĝ",
		"Ġ",
		"ġ",
		"Ģ",
		"ģ",
		"Ǧ",
		"ǧ",
		"Ḡ",
		"ḡ",
		"Ǥ",
		"ǥ",
		"Ɠ",
		"Γ",
		"γ",
		"Г",
		"г",
		"ɡ",
		"ɢ",
	],
	H: [
		"H",
		"h",
		"Ĥ",
		"ĥ",
		"Ħ",
		"ħ",
		"Ḣ",
		"ḣ",
		"Ḥ",
		"ḥ",
		"Ḧ",
		"ḧ",
		"Ḩ",
		"ḩ",
		"Ḫ",
		"ḫ",
		"Ȟ",
		"ȟ",
		"Η",
		"η",
		"Н",
		"н",
		"ʜ",
	],
	I: [
		"I",
		"i",
		"Í",
		"Ì",
		"Î",
		"Ï",
		"İ",
		"Ī",
		"Į",
		"Ǐ",
		"Ȉ",
		"Ȋ",
		"Ĩ",
		"Ĭ",
		"í",
		"ì",
		"î",
		"ï",
		"ī",
		"į",
		"ǐ",
		"ȉ",
		"ȋ",
		"ĩ",
		"ĭ",
		"Ỉ",
		"Ị",
		"Ḯ",
		"ỉ",
		"ị",
		"ḯ",
		"Ι",
		"І",
		"і",
		"Ϊ",
	],
	J: ["J", "j", "Ĵ", "ĵ", "ǰ", "Ɉ", "ɉ", "Ј", "ј", "ᴊ"],
	K: [
		"K",
		"k",
		"Ḱ",
		"ḱ",
		"Ķ",
		"ķ",
		"Ǩ",
		"ǩ",
		"Ḳ",
		"ḳ",
		"Ḵ",
		"ḵ",
		"Ƙ",
		"Κ",
		"κ",
		"К",
		"к",
		"ᴋ",
	],
	L: [
		"L",
		"l",
		"Ł",
		"ł",
		"Ĺ",
		"ĺ",
		"Ļ",
		"ļ",
		"Ľ",
		"ľ",
		"Ŀ",
		"ŀ",
		"Ƚ",
		"ƚ",
		"Ḷ",
		"Ḹ",
		"Ḻ",
		"Ḽ",
		"ḷ",
		"ḹ",
		"ḻ",
		"ḽ",
		"ʟ",
	],
	M: ["M", "m", "Ḿ", "Ṁ", "Ṃ", "ḿ", "ṁ", "ṃ", "Μ", "М", "м", "ᴍ", "ɱ"],
	N: [
		"N",
		"n",
		"Ń",
		"ń",
		"Ñ",
		"ñ",
		"Ň",
		"ň",
		"Ņ",
		"ņ",
		"Ṅ",
		"ṅ",
		"Ṇ",
		"ṇ",
		"Ṉ",
		"ṉ",
		"Ṋ",
		"ṋ",
		"Ǹ",
		"ǹ",
		"Ɲ",
		"ƞ",
		"Ν",
		"Н",
		"н",
		"ɴ",
	],
	O: [
		"O",
		"o",
		"Ó",
		"Ò",
		"Ô",
		"Ö",
		"Õ",
		"Ø",
		"Ō",
		"Ő",
		"Ǒ",
		"Ǿ",
		"Ȍ",
		"Ȏ",
		"Ȫ",
		"Ȭ",
		"Ȯ",
		"Ȱ",
		"ó",
		"ò",
		"ô",
		"ö",
		"õ",
		"ø",
		"ō",
		"ő",
		"ǒ",
		"ǿ",
		"ȍ",
		"ȏ",
		"ȫ",
		"ȭ",
		"ȯ",
		"ȱ",
		"Ớ",
		"Ờ",
		"Ở",
		"Ỡ",
		"Ợ",
		"Ố",
		"Ồ",
		"Ổ",
		"Ỗ",
		"Ộ",
		"Ọ",
		"Ỏ",
		"ớ",
		"ờ",
		"ở",
		"ỡ",
		"ợ",
		"ố",
		"ồ",
		"ổ",
		"ỗ",
		"ộ",
		"ọ",
		"ỏ",
		"0",
		"Ο",
		"ο",
		"О",
		"о",
		"ϴ",
		"ɵ",
		"ᴏ",
		"º",
	],
	P: ["P", "p", "Ṕ", "ṕ", "Ṗ", "ṗ", "Þ", "þ", "Π", "π", "П", "п", "ᴘ", "₱"],
	Q: ["Q", "q", "Ɋ", "ɋ", "ʠ", "ℚ", "Ǫ", "ǫ", "Ǭ", "ǭ"],
	R: [
		"R",
		"r",
		"Ŕ",
		"ŕ",
		"Ř",
		"ř",
		"Ŗ",
		"ŗ",
		"Ɍ",
		"ɍ",
		"Ȑ",
		"Ȓ",
		"Ṙ",
		"Ṛ",
		"Ṝ",
		"Ṟ",
		"ȑ",
		"ȓ",
		"ṙ",
		"ṛ",
		"ṝ",
		"ṟ",
		"®",
		"ʀ",
	],
	S: [
		"S",
		"s",
		"Ś",
		"ś",
		"Ŝ",
		"ŝ",
		"Ş",
		"ş",
		"Š",
		"š",
		"Ș",
		"ș",
		"Ṡ",
		"ṡ",
		"Ṣ",
		"ṣ",
		"Ṥ",
		"ṥ",
		"Ṧ",
		"ṧ",
		"Ṩ",
		"ṩ",
		"ſ",
		"Σ",
		"σ",
		"ς",
		"Ѕ",
		"ѕ",
		"ꜱ",
		"$",
	],
	T: [
		"T",
		"t",
		"Ť",
		"ť",
		"Ţ",
		"ţ",
		"Ț",
		"ț",
		"Ṫ",
		"ṫ",
		"Ṭ",
		"ṭ",
		"Ṯ",
		"ṯ",
		"Ṱ",
		"ṱ",
		"Ŧ",
		"ŧ",
		"Τ",
		"τ",
		"Т",
		"т",
		"ᴛ",
	],
	U: [
		"U",
		"u",
		"Ú",
		"Ù",
		"Û",
		"Ü",
		"Ũ",
		"Ū",
		"Ŭ",
		"Ů",
		"Ű",
		"Ų",
		"Ǔ",
		"Ǖ",
		"Ǘ",
		"Ǚ",
		"Ǜ",
		"ú",
		"ù",
		"û",
		"ü",
		"ũ",
		"ū",
		"ŭ",
		"ů",
		"ű",
		"ų",
		"ǔ",
		"ǖ",
		"ǘ",
		"ǚ",
		"ǜ",
		"Ứ",
		"Ừ",
		"Ử",
		"Ữ",
		"Ự",
		"Ụ",
		"Ủ",
		"ứ",
		"ừ",
		"ử",
		"ữ",
		"ự",
		"ụ",
		"ủ",
		"Ư",
		"ư",
		"Υ",
		"υ",
		"У",
		"у",
		"ᴜ",
	],
	V: ["V", "v", "Ṽ", "ṽ", "Ṿ", "ṿ", "ν", "Ѵ", "ѵ", "Ѷ", "ѷ", "ᴠ", "ʋ"],
	W: [
		"W",
		"w",
		"Ẃ",
		"ẃ",
		"Ẁ",
		"ẁ",
		"Ŵ",
		"ŵ",
		"Ẅ",
		"ẅ",
		"Ẇ",
		"ẇ",
		"Ẉ",
		"ẉ",
		"ω",
		"ᴡ",
	],
	X: ["X", "x", "Ẋ", "ẋ", "Ẍ", "ẍ", "Χ", "χ", "Х", "х", "×"],
	Y: [
		"Y",
		"y",
		"Ý",
		"ý",
		"Ỳ",
		"ỳ",
		"Ŷ",
		"ŷ",
		"Ÿ",
		"ÿ",
		"Ȳ",
		"ȳ",
		"Ẏ",
		"ẏ",
		"Ỵ",
		"ỵ",
		"Ỷ",
		"ỷ",
		"Ỹ",
		"ỹ",
		"¥",
		"Υ",
		"у",
		"ʏ",
	],
	Z: [
		"Z",
		"z",
		"Ź",
		"ź",
		"Ż",
		"ż",
		"Ž",
		"ž",
		"Ẑ",
		"ẑ",
		"Ẓ",
		"ẓ",
		"Ẕ",
		"ẕ",
		"Ƶ",
		"ƶ",
		"Ζ",
		"ζ",
		"З",
		"з",
		"ᴢ",
	],
};

export function variantKey(variant: LetterVariant) {
	return `${variant.char}:${variant.weight}:${variant.style}:${variant.caps}:${variant.stretch}`;
}

export const SETTLED: Omit<LetterVariant, "char"> = {
	weight: 900,
	style: "normal",
	caps: "normal",
	stretch: "normal",
};

export function applyLetterVariant(node: HTMLElement, variant: LetterVariant) {
	node.textContent = variant.char;
	node.style.fontWeight = String(variant.weight);
	node.style.fontStyle = variant.style;
	node.style.fontVariantCaps = variant.caps;
	node.style.fontStretch = variant.stretch;
	node.style.paddingInline = variant.style === "normal" ? "0" : "0.08em";
	node.style.fontFamily =
		variant.stretch === "condensed" || variant.stretch === "extra-condensed"
			? '"Helvetica Neue Condensed", "Helvetica Neue", Helvetica, sans-serif'
			: "";
}

export function pickVariant(
	letter: string,
	currentKey: string,
	casing: "any" | "lower" = "any",
) {
	const key = baseLetter(letter);
	let glyphs = LETTER_GLYPHS[key] ?? [key, key.toLowerCase()];
	if (casing === "lower") {
		glyphs = glyphs.filter((char) => char !== char.toUpperCase());
		if (!glyphs.length) glyphs = [key.toLowerCase()];
	}
	let next: LetterVariant = {
		char: glyphs[0],
		...SETTLED,
	};
	let guard = 0;
	do {
		const char = pick(glyphs);
		const slantOk = !NO_SLANT.has(char);
		next = {
			char,
			weight: pick(WEIGHTS),
			style: slantOk
				? pick(["normal", "italic", "oblique"] as const)
				: "normal",
			caps:
				char === char.toUpperCase()
					? "normal"
					: pick(["normal", "small-caps", "petite-caps"] as const),
			stretch: pick(STRETCHES),
		};
		guard += 1;
	} while (variantKey(next) === currentKey && guard < 8);
	return next;
}
